/**
 * Reine Listenlogik hinter "Zuletzt geöffnet" (Pfeil am Fälle-Button).
 *
 * Bewusst frei von Vue, Pinia und localStorage: Hinzufügen, Entfernen und
 * Abgleich mit dem Server sind so ohne Browser-Umgebung testbar
 * (frontend/tests/unit/recentCasesList.test.js). Der Store darüber kümmert
 * sich nur noch um Persistenz und Auth-Scope.
 *
 * 09.09.26 (Mail RA Polz): Bis hierher war die Liste eine Einbahnstraße —
 * Einträge wurden geschrieben, aber nie wieder entfernt. Nach dem endgültigen
 * Löschen einer Akte blieben Fall-ID und Mandantenname im localStorage stehen
 * und wurden weiter im Menü angezeigt. removeEntries/reconcileEntries schließen
 * genau diese Lücke.
 */

export interface RecentCaseEntry {
  /** Unverschlüsselte Fall-ID (für die Navigation ins Cockpit). */
  id: number;
  /** Anzeigename, z. B. Fallbezeichnung oder Aktenzeichen. */
  label: string;
  /** Zeitstempel des letzten Aufrufs (ms). */
  visitedAt: number;
}

/** Ein serverseitig noch existierender, für den Nutzer sichtbarer Fall. */
export interface KnownCase {
  id: number;
  label?: string | null;
}

export const MAX_RECENT = 10;

/** Fall-IDs sind positive Ganzzahlen; alles andere ist unbrauchbar. */
function toCaseId(value: unknown): number | null {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.trunc(num);
}

function fallbackLabel(id: number): string {
  return `Fall ${id}`;
}

/** localStorage-Rohwert in eine belastbare Liste überführen. */
export function parseEntries(raw: string | null | undefined): RecentCaseEntry[] {
  if (!raw) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const entries: RecentCaseEntry[] = [];
  for (const candidate of parsed) {
    const id = toCaseId((candidate as RecentCaseEntry)?.id);
    if (id === null) continue;
    const label = String((candidate as RecentCaseEntry)?.label || fallbackLabel(id));
    entries.push({ id, label, visitedAt: Number((candidate as RecentCaseEntry)?.visitedAt) || 0 });
    if (entries.length === MAX_RECENT) break;
  }
  return entries;
}

/**
 * Fall-Aufruf vorn einsortieren (dedupliziert, neueste zuerst, max. 10).
 * Ohne Label bleibt die bisher bekannte Bezeichnung erhalten.
 */
export function addVisit(
  entries: readonly RecentCaseEntry[],
  id: number | string | null | undefined,
  label?: string | null,
  visitedAt: number = Date.now(),
): RecentCaseEntry[] {
  const numId = toCaseId(id);
  if (numId === null) return [...entries];

  const known = entries.find((e) => e.id === numId);
  const entry: RecentCaseEntry = {
    id: numId,
    label: (label && String(label).trim()) || known?.label || fallbackLabel(numId),
    visitedAt,
  };
  return [entry, ...entries.filter((e) => e.id !== numId)].slice(0, MAX_RECENT);
}

/** Einen oder mehrere Fälle aus der Liste werfen (z. B. nach dem Löschen). */
export function removeEntries(
  entries: readonly RecentCaseEntry[],
  ids: number | string | ReadonlyArray<number | string>,
): RecentCaseEntry[] {
  const list = Array.isArray(ids) ? ids : [ids as number | string];
  const removed = new Set<number>();
  for (const value of list) {
    const id = toCaseId(value);
    if (id !== null) removed.add(id);
  }
  if (removed.size === 0) return [...entries];
  return entries.filter((e) => !removed.has(e.id));
}

/**
 * Liste gegen den Server abgleichen: Fälle, die es nicht mehr gibt oder auf
 * die kein Zugriff mehr besteht, verschwinden; Bezeichnungen umbenannter Akten
 * werden nachgezogen. Reihenfolge und Besuchszeitpunkte bleiben erhalten.
 *
 * `requestedIds` sind die IDs, über die die Antwort tatsächlich Auskunft gibt.
 * Über alles andere sagt sie nichts aus und es bleibt unangetastet — sonst
 * würde ein Fall, den der Nutzer während der laufenden Anfrage öffnet, von der
 * veralteten Antwort wieder aus der Historie geworfen. Ohne Angabe gelten alle
 * übergebenen Einträge als angefragt.
 *
 * Wichtig: Nur ein echtes Array gilt als Serverantwort. Bei null/Fehler bleibt
 * die Liste unverändert — ein Netzwerkfehler darf die Historie nicht löschen.
 */
export function reconcileEntries(
  entries: readonly RecentCaseEntry[],
  knownCases: ReadonlyArray<KnownCase> | null | undefined | unknown,
  requestedIds?: ReadonlyArray<number | string> | null,
): RecentCaseEntry[] {
  if (!Array.isArray(knownCases)) return [...entries];

  const labelById = new Map<number, string | null>();
  for (const known of knownCases as ReadonlyArray<KnownCase>) {
    const id = toCaseId(known?.id);
    if (id === null) continue;
    const label = known?.label ? String(known.label).trim() : "";
    labelById.set(id, label || null);
  }

  let asked: Set<number> | null = null;
  if (Array.isArray(requestedIds)) {
    asked = new Set<number>();
    for (const value of requestedIds) {
      const id = toCaseId(value);
      if (id !== null) asked.add(id);
    }
  }
  const wasAsked = (id: number) => asked === null || asked.has(id);

  return entries
    .filter((e) => !wasAsked(e.id) || labelById.has(e.id))
    .map((e) => ({ ...e, label: labelById.get(e.id) || e.label }));
}
